import { EChartsOption } from 'echarts'
import ReactEcharts from 'echarts-for-react'
import { useTranslation } from 'react-i18next'

import { BasicTable, ContentCard } from '@/components/customComponents'

import { lineChartOptions } from '@/constants/CHART_CONSTANTS'
import { chartColors } from '@/constants/COLOR_PALETTE'
import { formatNumber } from '@/utils/utils'

interface ProfitabilityTabProps {
  itemName: string
}

const MOCK_METRICS_DATA = [
  {
    metric: 'Avg Sales Price',
    value: '$3.45',
  },
  {
    metric: 'Planned vs Actual COGS',
    value: '$2.13 vs ($2.34)',
  },
  {
    metric: 'Gross Margin %',
    value: '62% vs (%68)',
  },
  {
    metric: 'Units Sold',
    value: '6,578',
  },
  {
    metric: 'Lost Profit',
    value: '$2,224',
  },
]

const MOCK_30_DAY_MARGIN_TREND_DATA = {
  Actual: [
    { month: 'w1', price: 1800.5 },
    { month: 'w2', price: 1810.5 },
    { month: 'w3', price: 1720.5 },
    { month: 'w4', price: 1930.5 },
  ],
  Planned: [
    { month: 'w1', price: 1700.5 },
    { month: 'w2', price: 1710.5 },
    { month: 'w3', price: 1720.5 },
    { month: 'w4', price: 1730.5 },
  ],
}

const MOCK_INGREDIENT_COST_BREAKDOWN_DATA = [
  {
    ingredient: 'Espresso Shot',
    plannedPortion: '1 Shot',
    plannedUnitCost: 0.35,
    actualUnitCost: 0.37,
    cogsImpact: -0.02,
    cogsImpact30DTotal: -264,
  },
  {
    ingredient: 'Oat Milk',
    plannedPortion: '180ml',
    plannedUnitCost: 0.7,
    actualUnitCost: 0.75,
    cogsImpact: -0.1,
    cogsImpact30DTotal: -1320,
  },
  {
    ingredient: 'Cinnamon Syrup',
    plannedPortion: '25ml',
    plannedUnitCost: 0.25,
    actualUnitCost: 0.26,
    cogsImpact: -0.03,
    cogsImpact30DTotal: -396,
  },
  {
    ingredient: 'Lid',
    plannedPortion: '1 unit',
    plannedUnitCost: 0.7,
    actualUnitCost: 0.7,
    cogsImpact: 0,
    cogsImpact30DTotal: 0,
  },
  {
    ingredient: 'Cup',
    plannedPortion: '1 unit',
    plannedUnitCost: 0.15,
    actualUnitCost: 0.17,
    cogsImpact: -0.02,
    cogsImpact30DTotal: -264,
  },
]

export const ProfitabilityTab = ({ itemName }: ProfitabilityTabProps) => {
  const { t } = useTranslation()
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <BasicTable
          title={t('clarityLens.salesLens.charts.profitabilityMetrics')}
          data={MOCK_METRICS_DATA}
          columns={[
            {
              header: 'Metric',
              accessorKey: 'metric',
              type: 'text',
            },
            {
              header: 'Value',
              accessorKey: 'value',
              type: 'text',
            },
          ]}
          loading={false}
          enablePagination
          itemsPerPage={5}
          isBeta
        />
        <ContentCard
          title={t('clarityLens.salesLens.charts.marginTrend30Day')}
          loading={false}
          isBeta
        >
          <ReactEcharts
            option={getLineChartData(MOCK_30_DAY_MARGIN_TREND_DATA)}
          />
        </ContentCard>
        <ContentCard
          title={t('clarityLens.salesLens.charts.competitorPriceComparison')}
          loading={false}
          isBeta
        >
          {true && (
            <ReactEcharts
              option={getBarChartData([
                { name: 'Our Price', value: 3.45 },
                { name: 'Starbucks', value: 2.75 },
                { name: 'Dunkin', value: 4.3 },
                { name: 'Dutch Bros', value: 3.15 },
                { name: '7Brew', value: 3.3 },
              ])}
            />
          )}
        </ContentCard>
      </div>

      <div>
        {/* TODO: do a badge component for status also update column logic for it */}
        <BasicTable
          title={t('clarityLens.salesLens.charts.ingredientCostBreakdown')}
          data={MOCK_INGREDIENT_COST_BREAKDOWN_DATA}
          columns={[
            {
              header: 'Ingredient',
              accessorKey: 'ingredient',
              type: 'text',
            },
            {
              header: 'Planned Portion',
              accessorKey: 'plannedPortion',
              type: 'text',
            },
            {
              header: 'Planned Unit Cost',
              accessorKey: 'plannedUnitCost',
              type: 'money',
            },
            {
              header: 'Actual Unit Cost',
              accessorKey: 'actualUnitCost',
              type: 'money',
            },
            {
              header: 'COGS Impact (per unit)',
              accessorKey: 'cogsImpact',
              type: 'money',
            },
            {
              header: 'COGS Impact (30D Total)',
              accessorKey: 'cogsImpact30DTotal',
              type: 'money',
            },
          ]}
          loading={false}
          enablePagination
          itemsPerPage={5}
          isBeta
        />
      </div>
    </div>
  )
}

const getLineChartData = (data: typeof MOCK_30_DAY_MARGIN_TREND_DATA) => {
  // Safety check for empty data
  if (!data || Object.keys(data).length === 0) {
    return lineChartOptions([], [], [], 'money')
  }

  const periods = Object.keys(data) // These will be our line names/series
  const allMonths = new Set<string>()

  // Collect all unique months (these will be our X-axis data points)
  periods.forEach((period) => {
    if (
      data[period as keyof typeof data] &&
      Array.isArray(data[period as keyof typeof data])
    ) {
      data[period as keyof typeof data].forEach((item) => {
        if (item && item.month) {
          allMonths.add(item.month)
        }
      })
    }
  })

  const monthNames = Array.from(allMonths) // Months should be in their natural order

  // Safety check for no months found
  if (monthNames.length === 0) {
    return lineChartOptions([], [], [], 'money')
  }

  // Create raw data for each period (line)
  const rawData = periods.map((period) => {
    return monthNames.map((month) => {
      const monthData = data[period as keyof typeof data]?.find(
        (item: any) => item?.month === month
      )
      return monthData ? monthData.price : 0
    })
  })

  return lineChartOptions(rawData, periods, monthNames, 'money')
}

const getBarChartData = (storeData: { name: string; value: number }[]) => {
  const xAxisNames = storeData.map((item) => item.name)

  // Determine if labels should be rotated based on label length or count
  const crossLabels =
    xAxisNames.some((name) => name.length > 10) || xAxisNames.length > 4

  // Create a custom chart option with different colors for each competitor
  return getCustomBarChartOptions(storeData, crossLabels)
}

const getCustomBarChartOptions = (
  storeData: { name: string; value: number }[],
  crossLabels: boolean
): EChartsOption => {
  const xAxisNames = storeData.map((item) => item.name)

  // Define color mapping for different competitors
  const getColorForItem = (name: string, index: number) => {
    if (name.toLowerCase().includes('our price')) {
      return chartColors[0] // Blue for our price
    } else if (name.toLowerCase().includes('competitor 1')) {
      return chartColors[1] // Green for competitor 1
    } else if (name.toLowerCase().includes('competitor 2')) {
      return chartColors[3] // Red for competitor 2
    }
    // Fallback to index-based color
    return chartColors[index % chartColors.length]
  }

  return {
    tooltip: {
      trigger: 'item',
      formatter: (params: any) => {
        const formattedValue = formatNumber(params.value, 'money')
        return `${params.name}<br/>Price: ${formattedValue}`
      },
    },
    grid: {
      top: '5%',
      bottom: '25%',
      left: '12%',
      right: '4%',
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: '${value}',
      },
    },
    xAxis: {
      type: 'category',
      data: xAxisNames,
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        rotate: crossLabels ? 45 : xAxisNames.length > 5 ? 45 : 0,
        interval: 0,
      },
    },
    series: [
      {
        name: 'Price',
        type: 'bar',
        barWidth: '60%',
        data: storeData.map((item, index) => ({
          value: item.value,
          itemStyle: {
            borderRadius: 8,
            borderWidth: 1,
            borderColor: '#fff',
            color: getColorForItem(item.name, index),
          },
        })),
      },
    ],
  }
}
